import { Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Button } from './button';
import { Tooltip } from './overlay';

export interface BulkAction {
  label: string;
  icon?: ReactNode;
  tone?: 'default' | 'danger';
  onAction: () => void | Promise<void>;
  /** Shown as a tooltip and disables the button, e.g. "System roles can't be deleted". */
  disabledReason?: string;
}

export interface BulkActionBarProps {
  /** How many rows are selected. Nothing renders at 0. */
  count: number;
  onClearSelection: () => void;
  actions: BulkAction[];
  busy?: boolean;
  className?: string;
}

/** The Odoo-style "N selected" bar under a `DataTable`: appears once anything is checked, offers the caller's own actions. */
export function BulkActionBar({ count, onClearSelection, actions, busy, className }: BulkActionBarProps) {
  if (count === 0) return null;
  return (
    <div
      role="toolbar"
      aria-label={`${count} selected`}
      className={cn('sticky bottom-4 z-10 flex flex-wrap items-center gap-3 rounded-lg border bg-surface px-4 py-2.5 shadow-lg', className)}
    >
      <span className="text-base font-medium text-fg">{count} selected</span>
      <button type="button" onClick={onClearSelection} disabled={busy} className="text-sm font-medium text-brand-text hover:underline disabled:opacity-50">
        Clear
      </button>
      <span className="ml-auto flex flex-wrap items-center gap-2">
        {busy ? <Loader2 className="size-4 shrink-0 animate-spin text-muted" aria-hidden /> : null}
        {actions.map((action) => {
          const button = (
            <Button
              key={action.label}
              size="sm"
              variant={action.tone === 'danger' ? 'danger-ghost' : 'ghost'}
              leadingIcon={action.icon}
              disabled={busy || !!action.disabledReason}
              onClick={() => void action.onAction()}
            >
              {action.label}
            </Button>
          );
          return action.disabledReason ? (
            <Tooltip key={action.label} content={action.disabledReason}>
              {button}
            </Tooltip>
          ) : (
            button
          );
        })}
      </span>
    </div>
  );
}
